import { useState } from 'react'
import {
  AlertTriangleIcon,
  ChevronDownIcon,
  PlusIcon,
  SquarePlusIcon,
  Trash2Icon
} from 'lucide-react'
import {
  describeCondition,
  isConditionLeaf,
  LEAF_KIND_BADGE,
  type Condition,
  type ConditionAnd,
  type ConditionEvalNode,
  type ConditionLeaf,
  type ConditionNot,
  type ConditionOr,
  type TriEvalState
} from '@axilon-apps/domains-ontology-ui-state'

import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group'
import { cn } from '../../lib/utils'
import { formatTagShort } from '../../pages/ConfigurePage/util/tag-paths'
import { DefRow, LeafEvaluationView } from './TriggerEvaluationList'
import { TriggerSparkline } from './TriggerSparkline'
import { describeLeafCondition, formatLeafCurrent } from './trigger-language'
import { defaultAnd, defaultLeaf } from './condition-defaults'

/** Append a backend eval path segment (`children.N` / `child`). Root stays `""`. */
export function childEvalPath(parent: string, segment: string): string {
  if (!parent) return segment
  return `${parent}.${segment}`
}

/** Index DFS-ordered evaluation nodes by backend path. */
export function indexEvalNodesByPath(
  nodes: ConditionEvalNode[] | undefined
): Map<string, ConditionEvalNode> {
  const map = new Map<string, ConditionEvalNode>()
  for (const n of nodes ?? []) {
    map.set(n.path, n)
  }
  return map
}

export function conditionNodeStateLabel(state: TriEvalState | undefined): string {
  if (state === 'fired') return 'Met'
  if (state === 'not_fired') return 'Healthy'
  if (state === 'error') return 'Error'
  return 'Unknown'
}

/** Visual bucket for a tree node when an eval transcript is present. */
export type EvalTone = 'fired' | 'unhealthy' | 'healthy' | 'neutral'

/** Map a tri-state onto firing (red) vs unhealthy (amber) vs healthy. */
export function evalTone(state: TriEvalState | undefined, showEval: boolean): EvalTone {
  if (!showEval) return 'neutral'
  if (state === 'fired') return 'fired'
  if (state === 'not_fired') return 'healthy'
  return 'unhealthy'
}

export function evalToneClasses(tone: EvalTone, kind: 'leaf' | 'group'): string {
  if (tone === 'fired') {
    return 'border-st-down/60 bg-st-down-bg/55'
  }
  if (tone === 'unhealthy') {
    return 'border-st-warn/65 bg-st-warn-bg/60'
  }
  if (tone === 'healthy') {
    return 'border-st-run/30 bg-st-run-bg/25'
  }
  return kind === 'group' ? 'border-border bg-muted/30' : 'border-border bg-muted/20'
}

export function railToneClass(tone: EvalTone | undefined): string {
  if (tone === 'fired') return 'border-l-2 border-st-down'
  if (tone === 'unhealthy') return 'border-l-2 border-st-warn'
  if (tone === 'healthy') return 'border-l border-st-run/40'
  return 'border-l border-border'
}

export interface ConditionTreeEditorProps {
  condition: Condition
  readOnly?: boolean
  /** Optional live eval transcript for the current trigger (DFS order). */
  evalNodes?: ConditionEvalNode[]
  collapsed: Record<string, boolean>
  onToggleCollapse: (key: string) => void
  onChange: (next: Condition) => void
  onAddLeaf: (parentPath: number[]) => void
  onEditLeaf: (parentPath: number[], leafIndex: number, leaf: ConditionLeaf) => void
}

export function ConditionTreeEditor({
  condition,
  readOnly = false,
  evalNodes,
  collapsed,
  onToggleCollapse,
  onChange,
  onAddLeaf,
  onEditLeaf
}: ConditionTreeEditorProps) {
  const evalByPath = indexEvalNodesByPath(evalNodes)
  const showEval = evalNodes !== undefined

  return (
    <NodeView
      condition={condition}
      depth={0}
      pathKey="root"
      path={[]}
      evalPath=""
      isRoot
      readOnly={readOnly}
      showEval={showEval}
      ancestorTones={[]}
      evalByPath={evalByPath}
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      onChange={onChange}
      onAddLeaf={onAddLeaf}
      onEditLeaf={onEditLeaf}
      onDelete={undefined}
    />
  )
}

interface NodeViewProps {
  condition: Condition
  depth: number
  pathKey: string
  path: number[]
  evalPath: string
  isRoot: boolean
  readOnly: boolean
  showEval: boolean
  ancestorTones: EvalTone[]
  evalByPath: Map<string, ConditionEvalNode>
  collapsed: Record<string, boolean>
  onToggleCollapse: (key: string) => void
  onChange: (next: Condition) => void
  onAddLeaf: (parentPath: number[]) => void
  onEditLeaf: (parentPath: number[], leafIndex: number, leaf: ConditionLeaf) => void
  onDelete?: () => void
}

function NodeView({
  condition,
  depth,
  pathKey,
  path,
  evalPath,
  isRoot,
  readOnly,
  showEval,
  ancestorTones,
  evalByPath,
  collapsed,
  onToggleCollapse,
  onChange,
  onAddLeaf,
  onEditLeaf,
  onDelete
}: NodeViewProps) {
  const evalNode = showEval ? evalByPath.get(evalPath) : undefined
  const thisTone = evalTone(evalNode?.state, showEval)
  const railTones = showEval && depth > 0 ? [...ancestorTones, thisTone] : undefined
  const childAncestorTones = depth === 0 ? ancestorTones : [...ancestorTones, thisTone]

  if (isConditionLeaf(condition)) {
    return (
      <>
        <LeafRow
          leaf={condition}
          depth={depth}
          railTones={railTones}
          readOnly={readOnly}
          showEval={showEval}
          evalNode={evalNode}
          onEdit={() => {
            if (isRoot) {
              // Edit replaces the root leaf in place via parentPath=[] leafIndex=0 convention.
              onEditLeaf([], 0, condition)
            } else {
              const leafIndex = path[path.length - 1] ?? 0
              onEditLeaf(path.slice(0, -1), leafIndex, condition)
            }
          }}
          onDelete={!isRoot && !readOnly ? onDelete : undefined}
        />
        {isRoot && !readOnly && (
          <div className="flex items-center gap-2 px-0.5 py-2">
            <span className="text-11 text-muted-foreground/70">Combine with:</span>
            {(['and', 'or', 'not'] as const).map((op) => (
              <Button
                key={op}
                type="button"
                variant="outline"
                size="sm"
                className="h-7 border-dashed uppercase"
                onClick={() => {
                  if (op === 'not') onChange({ kind: 'not', operand: condition })
                  else onChange({ kind: op, operands: [condition, defaultLeaf('live')] })
                }}
              >
                {op}
              </Button>
            ))}
          </div>
        )}
      </>
    )
  }

  if (condition.kind === 'not') {
    return (
      <GroupBlock
        op="not"
        depth={depth}
        railTones={railTones}
        open={!collapsed[pathKey]}
        readOnly={readOnly}
        canDelete={!isRoot}
        showEval={showEval}
        evalState={evalNode?.state}
        evalReason={evalNode?.reason}
        onToggle={() => onToggleCollapse(pathKey)}
        onSetOp={(op) => {
          if (op === 'not') return
          onChange({
            kind: op,
            operands: [condition.operand],
            description: condition.description
          })
        }}
        onDelete={onDelete}
      >
        {!collapsed[pathKey] && (
          <NodeView
            condition={condition.operand}
            depth={depth + 1}
            pathKey={`${pathKey}.0`}
            path={[...path, 0]}
            evalPath={childEvalPath(evalPath, 'child')}
            isRoot={false}
            readOnly={readOnly}
            showEval={showEval}
            ancestorTones={childAncestorTones}
            evalByPath={evalByPath}
            collapsed={collapsed}
            onToggleCollapse={onToggleCollapse}
            onChange={(next) => onChange({ ...condition, operand: next })}
            onAddLeaf={onAddLeaf}
            onEditLeaf={onEditLeaf}
            onDelete={undefined}
          />
        )}
      </GroupBlock>
    )
  }

  const group = condition as ConditionAnd | ConditionOr
  const open = !collapsed[pathKey]
  const kids = group.operands

  return (
    <GroupBlock
      op={group.kind}
      depth={depth}
      railTones={railTones}
      open={open}
      readOnly={readOnly}
      canDelete={!isRoot}
      showEval={showEval}
      evalState={evalNode?.state}
      evalReason={evalNode?.reason}
      onToggle={() => onToggleCollapse(pathKey)}
      onSetOp={(op) => {
        if (op === 'not') {
          onChange({
            kind: 'not',
            operand: kids[0] ?? defaultLeaf('live'),
            description: group.description
          } satisfies ConditionNot)
          return
        }
        onChange({ ...group, kind: op })
      }}
      onDelete={onDelete}
    >
      {open &&
        kids.map((child, i) => (
          <NodeView
            key={i}
            condition={child}
            depth={depth + 1}
            pathKey={`${pathKey}.${i}`}
            path={[...path, i]}
            evalPath={childEvalPath(evalPath, `children.${i}`)}
            isRoot={false}
            readOnly={readOnly}
            showEval={showEval}
            ancestorTones={childAncestorTones}
            evalByPath={evalByPath}
            collapsed={collapsed}
            onToggleCollapse={onToggleCollapse}
            onChange={(next) => {
              const operands = kids.slice()
              operands[i] = next
              onChange({ ...group, operands })
            }}
            onAddLeaf={onAddLeaf}
            onEditLeaf={onEditLeaf}
            onDelete={
              readOnly
                ? undefined
                : () => {
                    const operands = kids.filter((_, j) => j !== i)
                    if (operands.length === 0) {
                      onChange({ ...group, operands: [] })
                      return
                    }
                    if (operands.length === 1 && isRoot) {
                      onChange(operands[0])
                      return
                    }
                    onChange({ ...group, operands })
                  }
            }
          />
        ))}
      {open && !readOnly && (
        <div className="flex items-stretch">
          <RailSpacer
            depth={depth + 1}
            tones={showEval ? [...childAncestorTones, thisTone] : undefined}
          />
          <div className="flex gap-2 px-0.5 py-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 border-dashed"
              onClick={() => onAddLeaf(path)}
            >
              <PlusIcon className="size-3.5" />
              Condition
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 border-dashed text-muted-foreground"
              onClick={() => onChange({ ...group, operands: [...kids, defaultAnd()] })}
            >
              <SquarePlusIcon className="size-3.5" />
              Group
            </Button>
          </div>
        </div>
      )}
    </GroupBlock>
  )
}

function GroupBlock({
  op,
  depth,
  railTones,
  open,
  readOnly,
  canDelete,
  showEval,
  evalState,
  evalReason,
  onToggle,
  onSetOp,
  onDelete,
  children
}: {
  op: 'and' | 'or' | 'not'
  depth: number
  railTones?: EvalTone[]
  open: boolean
  readOnly: boolean
  canDelete: boolean
  showEval: boolean
  evalState?: TriEvalState
  evalReason?: string
  onToggle: () => void
  onSetOp: (op: 'and' | 'or' | 'not') => void
  onDelete?: () => void
  children: React.ReactNode
}) {
  const tone = evalTone(evalState, showEval)
  return (
    <div className="flex items-stretch">
      <RailSpacer depth={depth} tones={railTones} />
      <div className="min-w-0 flex-1">
        <div
          data-eval-tone={showEval ? tone : undefined}
          className={cn(
            'my-1 flex items-center gap-2.5 rounded-lg border px-2.5 py-2',
            evalToneClasses(tone, 'group')
          )}
        >
          <button
            type="button"
            onClick={onToggle}
            className="flex size-5 items-center justify-center text-muted-foreground"
            aria-label={open ? 'Collapse group' : 'Expand group'}
          >
            <ChevronDownIcon
              className={cn('size-3.5 transition-transform', !open && '-rotate-90')}
            />
          </button>
          {readOnly ? (
            <ReadOnlyOpBadge op={op} tone={showEval ? tone : undefined} />
          ) : (
            <ToggleGroup
              type="single"
              value={op}
              onValueChange={(v) => {
                if (!v) return
                if (v === 'and' || v === 'or' || v === 'not') onSetOp(v)
              }}
              variant="outline"
              size="sm"
            >
              <ToggleGroupItem value="and" className="h-7 px-2.5 text-xs">
                AND
              </ToggleGroupItem>
              <ToggleGroupItem value="or" className="h-7 px-2.5 text-xs">
                OR
              </ToggleGroupItem>
              <ToggleGroupItem value="not" className="h-7 px-2.5 text-xs">
                NOT
              </ToggleGroupItem>
            </ToggleGroup>
          )}
          <span className="flex-1" />
          {showEval && <EvalStateLabel state={evalState} detail={evalReason} />}
          {canDelete && onDelete && !readOnly && (
            <Button type="button" variant="ghost" size="icon-sm" onClick={onDelete}>
              <Trash2Icon className="size-3.5" />
            </Button>
          )}
        </div>
        {children}
      </div>
    </div>
  )
}

function ReadOnlyOpBadge({ op, tone }: { op: 'and' | 'or' | 'not'; tone?: EvalTone }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md border bg-background px-2.5 py-1 text-xs font-semibold uppercase tracking-wider shadow-sm',
        tone === 'fired' && 'border-st-down/50 text-st-down',
        tone === 'unhealthy' && 'border-st-warn/50 text-st-warn',
        tone === 'healthy' && 'border-st-run/45 text-st-run',
        (tone === undefined || tone === 'neutral') && 'border-foreground/25 text-foreground'
      )}
    >
      {op}
    </span>
  )
}

function LeafRow({
  leaf,
  depth,
  railTones,
  readOnly,
  showEval,
  evalNode,
  onEdit,
  onDelete
}: {
  leaf: ConditionLeaf
  depth: number
  railTones?: EvalTone[]
  readOnly: boolean
  showEval: boolean
  evalNode?: ConditionEvalNode
  onEdit: () => void
  onDelete?: () => void
}) {
  const [expanded, setExpanded] = useState(false)
  const evalState = evalNode?.state
  const tone = evalTone(evalState, showEval)
  const summary = leaf.description?.trim() || describeCondition(leaf)
  const canInspect = readOnly && showEval && !!evalNode
  const leafDesc = evalNode ? describeLeafCondition(evalNode) : null
  const currentLabel = evalNode ? formatLeafCurrent(evalNode) : null

  const onActivate = () => {
    if (canInspect) {
      setExpanded((v) => !v)
      return
    }
    if (!readOnly) onEdit()
  }

  return (
    <div className="flex items-stretch">
      <RailSpacer depth={depth} tones={railTones} />
      <div
        data-eval-tone={showEval ? tone : undefined}
        className={cn(
          'my-1 min-w-0 flex-1 overflow-hidden rounded-lg border',
          evalToneClasses(tone, 'leaf')
        )}
      >
        <div
          role={canInspect || !readOnly ? 'button' : undefined}
          tabIndex={canInspect || !readOnly ? 0 : undefined}
          aria-expanded={canInspect ? expanded : undefined}
          onClick={canInspect || !readOnly ? onActivate : undefined}
          onKeyDown={
            canInspect || !readOnly
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onActivate()
                  }
                }
              : undefined
          }
          className={cn(
            'group/leaf flex min-w-0 items-center gap-2.5 px-2.5 py-2',
            (canInspect || !readOnly) &&
              'cursor-pointer transition-colors hover:border-foreground/25 hover:bg-foreground/[0.02]'
          )}
        >
          <EvalDot showEval={showEval} state={evalState} />
          <Badge
            variant="secondary"
            className="shrink-0 rounded-md px-1.5 text-10 uppercase tracking-wider"
          >
            {LEAF_KIND_BADGE[leaf.kind]}
          </Badge>
          <span className="min-w-0 flex-1 whitespace-normal break-words text-xs leading-snug text-foreground sm:text-13">
            {summary}
          </span>
          {showEval && currentLabel && (
            <span className="shrink-0 font-mono text-11 text-foreground/80">{currentLabel}</span>
          )}
          {showEval && !currentLabel && evalNode?.reason && (
            <span
              className={cn(
                'max-w-[10rem] shrink-0 truncate font-mono text-10',
                tone === 'unhealthy' ? 'text-st-warn' : 'text-muted-foreground'
              )}
              title={evalNode.reason}
            >
              {evalNode.reason}
            </span>
          )}
          {showEval && <EvalStateLabel state={evalState} detail={evalNode?.reason} />}
          {canInspect && (
            <ChevronDownIcon
              className={cn(
                'size-3.5 shrink-0 text-muted-foreground transition-transform duration-200',
                expanded && 'rotate-180'
              )}
              aria-hidden
            />
          )}
          {!readOnly && onDelete && (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="shrink-0 opacity-0 transition-opacity group-hover/leaf:opacity-100"
              onClick={(e) => {
                e.stopPropagation()
                onDelete()
              }}
            >
              <Trash2Icon className="size-3.5" />
            </Button>
          )}
        </div>

        {canInspect && evalNode && (
          <div
            className={cn(
              'grid transition-[grid-template-rows] duration-200 ease-out',
              expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
            )}
          >
            <div className="min-h-0 overflow-hidden">
              <LeafEvalDetails evalNode={evalNode} desc={leafDesc} />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function LeafEvalDetails({
  evalNode,
  desc
}: {
  evalNode: ConditionEvalNode
  desc: ReturnType<typeof describeLeafCondition>
}) {
  const isMet = evalNode.state === 'fired'
  return (
    <div className="flex flex-col gap-2.5 border-t border-border/70 bg-background/40 px-3 py-2.5">
      {evalNode.tag_path && (
        <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-xs">
          <DefRow label="Tag" value={formatTagShort(evalNode.tag_path)} mono />
        </dl>
      )}
      {desc && <LeafEvaluationView desc={desc} isFiring={isMet} />}
      {evalNode.reason && (
        <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-xs">
          <DefRow label="Detail" value={evalNode.reason} />
        </dl>
      )}
      {evalNode.history && evalNode.history.length > 0 && (
        <TriggerSparkline
          history={evalNode.history}
          threshold={typeof evalNode.value === 'number' ? evalNode.value : undefined}
          fired={isMet}
          width={220}
          height={32}
          className="max-w-full"
        />
      )}
    </div>
  )
}

function EvalDot({ showEval, state }: { showEval: boolean; state?: TriEvalState }) {
  if (!showEval) {
    return <span className="size-2 shrink-0 rounded-full border-[1.5px] border-foreground/25" />
  }
  if (state === 'error') {
    return <AlertTriangleIcon className="size-3 shrink-0 text-st-warn" aria-hidden />
  }
  if (state === 'fired') {
    return <span className="size-2 shrink-0 rounded-full bg-st-down" aria-hidden />
  }
  if (state === 'not_fired') {
    return <span className="size-2 shrink-0 rounded-full bg-st-run" aria-hidden />
  }
  return <span className="size-2 shrink-0 rounded-full bg-st-warn" aria-hidden />
}

function EvalStateLabel({
  state,
  detail,
  className
}: {
  state?: TriEvalState
  detail?: string
  className?: string
}) {
  const label = conditionNodeStateLabel(state)
  const title = detail?.trim() || undefined
  return (
    <span
      title={title}
      className={cn(
        'shrink-0 text-10 font-semibold uppercase tracking-wider',
        state === 'fired' && 'text-st-down',
        state === 'not_fired' && 'text-st-run',
        (state === 'error' || state === 'unknown' || state === undefined) && 'text-st-warn',
        className
      )}
    >
      {label}
    </span>
  )
}

function RailSpacer({ depth, tones }: { depth: number; tones?: EvalTone[] }) {
  if (depth <= 0) return null
  return (
    <>
      {Array.from({ length: depth }, (_, i) => {
        const tone = tones?.[i]
        return (
          <span
            key={i}
            data-rail-tone={tone}
            className={cn('ml-2 w-5.5 shrink-0', railToneClass(tone))}
          />
        )
      })}
    </>
  )
}
